import { useEffect, useRef, type MutableRefObject } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type { ContentTreeItem } from './data-transformers.ts';
import { isManagedFolder } from './data-transformers.ts';
import type { PageTreeState, QueryChildren } from './usePageTreeState.ts';
// usePageTreeState is imported as a value because it is referenced via `typeof` below to derive the
// actions type; oxlint's consistent-type-imports misflags this as type-only, but `import type` would
// break the `typeof` query.
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- see comment above
import { usePageTreeState } from './usePageTreeState.ts';

type PageTreeActions = ReturnType<typeof usePageTreeState>[1];

export type AutoMoveRef = MutableRefObject<{
	parentId: string | null;
	phase: 'pending' | 'running' | 'done';
}>;

export const findSyntheticItemParentId = (
	pages: PageTreeState['pages'],
	sourceId: string,
): string | undefined =>
	Object.keys(pages).find((id) => {
		const item = pages[id];
		const data = item?.data as { hasSyntheticChild?: boolean } | undefined;
		return data?.hasSyntheticChild || item?.children?.includes(sourceId);
	});

export const getAutoMoveExpansionErrorAttributes = (
	error: unknown,
): {
	errorName: string;
	errorCode: string;
} => {
	const maybeError = error as
		| {
				name?: unknown;
				extensions?: {
					code?: unknown;
					errorType?: unknown;
				};
		  }
		| undefined;
	const maybeExtensions = maybeError?.extensions;

	let errorCode = 'unknown';
	if (typeof maybeExtensions?.errorType === 'string') {
		errorCode = maybeExtensions.errorType;
	} else if (typeof maybeExtensions?.code === 'string') {
		errorCode = maybeExtensions.code;
	}

	let errorName = 'unknown';
	if (typeof maybeError?.name === 'string') {
		errorName = maybeError.name;
	} else if (typeof error === 'string') {
		errorName = 'string';
	}

	return { errorName, errorCode };
};

export const createAutoMoveExpansionQueryChildren = ({
	queryChildren,
	onExpansionFailure,
}: {
	queryChildren: QueryChildren;
	onExpansionFailure: (error: unknown) => void;
}): QueryChildren => {
	return async (...args) => {
		try {
			const result = await queryChildren(...args);
			if (result.error) {
				markErrorAsHandled(result.error);
				onExpansionFailure(result.error);
			}
			return result;
		} catch (error) {
			markErrorAsHandled(error);
			onExpansionFailure(error);
			return { nodes: [], error };
		}
	};
};

// The async portion of the auto-move (destination expansion + reparent) is extracted so it can be
// unit-tested directly by awaiting it. The synchronous guards and the `phase = 'running'` transition
// stay in the effect (see PageTreeCoordinator) so the cleanup's cancellation semantics are preserved.
export const runAutoMoveExpansion = async ({
	destinationPage,
	targetParentId,
	sourceId,
	actions,
	queryChildren,
	isMountedRef,
	autoMoveRef,
	getCancelled,
	createAnalyticsEvent,
	onMoved,
	position = 'first',
}: {
	destinationPage: ContentTreeItem;
	targetParentId: string;
	sourceId: string;
	actions: PageTreeActions;
	queryChildren: QueryChildren;
	isMountedRef: MutableRefObject<boolean>;
	autoMoveRef: AutoMoveRef;
	getCancelled: () => boolean;
	createAnalyticsEvent: ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'];
	/** Called once the page has been moved under the destination in the tree. */
	onMoved?: () => void;
	/** Where under the destination the page goes: first (suggestions) or after its existing children. */
	position?: 'first' | 'last';
}): Promise<void> => {
	const fireAutoMoveExpansionFailureEvent = (error: unknown) => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'failed',
				actionSubject: 'pageTreeRovoSuggestedParentAutoMoveExpansion',
				source: 'movePageDialog',
				attributes: getAutoMoveExpansionErrorAttributes(error),
			},
		}).fire();
	};
	try {
		if (isManagedFolder(destinationPage.data) || destinationPage.data.status === 'DRAFT') {
			return;
		}
		if (destinationPage.hasChildren && !actions.getState().expandedPages.has(targetParentId)) {
			try {
				const queryChildrenWithExpansionFailureEvent = createAutoMoveExpansionQueryChildren({
					queryChildren,
					onExpansionFailure: fireAutoMoveExpansionFailureEvent,
				});
				await actions.expandPage(destinationPage, queryChildrenWithExpansionFailureEvent);
			} catch (error) {
				// Destination expansion is best effort; the item can still move into the parent. Mark
				// the error as handled so an expandPage rejection that bypasses the wrapped queryChildren
				// path is not double-reported via the global error boundary.
				markErrorAsHandled(error);
				fireAutoMoveExpansionFailureEvent(error);
			}
			if (getCancelled()) {
				return;
			}
		}
		const { pages: currentPages } = actions.getState();
		const currentSourceParentId = findSyntheticItemParentId(currentPages, sourceId);
		if (!currentSourceParentId || currentSourceParentId === targetParentId) {
			return;
		}
		const index = currentPages[currentSourceParentId]?.children?.indexOf(sourceId) ?? -1;
		if (index === -1) {
			return;
		}
		if (!isMountedRef.current) {
			return;
		}
		if (autoMoveRef.current.parentId !== targetParentId) {
			return;
		}
		actions.movePageInState(
			sourceId,
			{ parentId: currentSourceParentId, index },
			{
				parentId: targetParentId,
				index: position === 'last' ? (currentPages[targetParentId]?.children?.length ?? 0) : 0,
			},
			queryChildren,
			true,
		);
		try {
			onMoved?.();
		} catch (error) {
			// Telling the caller where the page landed is best effort; the page has already moved.
			markErrorAsHandled(error);
		}
	} finally {
		if (!getCancelled() && autoMoveRef.current.parentId === targetParentId) {
			autoMoveRef.current.phase = 'done';
		}
	}
};

// Optimistically reparents the synthetic (moving) item under an assisted-suggestion destination as
// soon as that destination is available in the tree. Co-located with the auto-move helpers since it
// is only relevant to the move-page dialog.
export const useAutoMoveSyntheticItem = ({
	isMovePageTree,
	moveToParentId,
	syntheticItem,
	actions,
	queryChildren,
	isMountedRef,
	pages,
	onMoved,
	position,
}: {
	isMovePageTree?: boolean;
	moveToParentId?: string | null;
	syntheticItem?: ContentTreeItem;
	actions: PageTreeActions;
	queryChildren: QueryChildren;
	isMountedRef: MutableRefObject<boolean>;
	pages: PageTreeState['pages'];
	/** Called once the page has been moved under `moveToParentId` in the tree. */
	onMoved?: () => void;
	/** Where under `moveToParentId` the page goes. Defaults to first. */
	position?: 'first' | 'last';
}): void => {
	// Read the latest callback when the async move finishes, without re-running the effect.
	const onMovedRef = useRef(onMoved);
	useEffect(() => {
		onMovedRef.current = onMoved;
	}, [onMoved]);
	const autoMoveRef = useRef<{ parentId: string | null; phase: 'pending' | 'running' | 'done' }>({
		parentId: null,
		phase: 'pending',
	});
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (!isMovePageTree || !moveToParentId || !syntheticItem) {
			autoMoveRef.current = { parentId: null, phase: 'pending' };
			return;
		}
		if (autoMoveRef.current.parentId !== moveToParentId) {
			autoMoveRef.current = { parentId: moveToParentId, phase: 'pending' };
		}
		if (autoMoveRef.current.phase !== 'pending') {
			return;
		}
		const { pages: latestPages } = actions.getState();
		const destinationPage = latestPages[moveToParentId];
		if (!destinationPage) {
			return;
		}
		const sourceId = syntheticItem.id;
		const sourceParentId = findSyntheticItemParentId(latestPages, sourceId);
		if (!sourceParentId) {
			return;
		}

		autoMoveRef.current.phase = 'running';
		const targetParentId = moveToParentId;
		let cancelled = false;
		void runAutoMoveExpansion({
			destinationPage,
			targetParentId,
			sourceId,
			actions,
			queryChildren,
			isMountedRef,
			autoMoveRef,
			getCancelled: () => cancelled,
			createAnalyticsEvent,
			onMoved: () => onMovedRef.current?.(),
			position,
		});
		return () => {
			cancelled = true;
			if (
				autoMoveRef.current.parentId === targetParentId &&
				autoMoveRef.current.phase === 'running'
			) {
				autoMoveRef.current.phase = 'pending';
			}
		};
	}, [
		isMovePageTree,
		moveToParentId,
		pages,
		syntheticItem,
		actions,
		queryChildren,
		isMountedRef,
		createAnalyticsEvent,
		position,
	]);
};
